import { defineStore } from 'pinia';
import { API } from '@vp/common';
import { ref } from 'vue';
import { useConnectionStore } from '@/stores/connection';
import { LoadingState } from '@/util/LoadingState';
import { take } from '@/helpers/takePayload';

type AddArtistParams = {
  id?: number;
  name?: string;
  soundchartsUuid?: string;
  platformUrl?: string;
};

const isUrl = (url: string) => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

export const useEventArtistStore = defineStore('eventArtists', () => {
  const connectionStore = useConnectionStore();
  const apiClient = connectionStore.apiClient!;

  const eventGroupArtists = ref<Record<number, API.EventGroupArtist.EventGroupArtist.EventGroupArtist[]>>({});
  const eventGroupArtistsLoading = ref<Record<number, LoadingState>>({});
  const eventGroupArtistSaving = ref<Record<number, LoadingState>>({});

  // const eventArtists = ref<Record<number, API.EventGroupArtist.EventGroupArtist.EventGroupArtist[]>>({});
  // const eventArtistsLoading = ref<Record<number, LoadingState>>({});
  const eventArtistSaving = ref<Record<number, LoadingState>>({});
  const eventArtistRemoving = ref<Record<number, LoadingState>>({});
  const eventArtistsReordering = ref<Record<number, LoadingState>>({});

  const loadEventGroupArtists = async (eventGroupId: number) => {
    try {
      eventGroupArtistsLoading.value[eventGroupId] = LoadingState.LOADING;

      eventGroupArtists.value[eventGroupId] = (await API.EventGroupArtist.Api.fetchEventGroupArtists(apiClient, eventGroupId)).collection;

      eventGroupArtistsLoading.value[eventGroupId] = LoadingState.DONE;
    } catch (e) {
      eventGroupArtistsLoading.value[eventGroupId] = LoadingState.ERROR;
      throw e;
    }
  };

  const addEventGroupArtist = async (eventGroupId: number, artist: AddArtistParams) => {
    try {
      eventGroupArtistSaving.value[eventGroupId] = LoadingState.LOADING;
      if (!artist.platformUrl && isUrl(artist.name!)) {
        artist.platformUrl = artist.name;
      }

      const { success } = await API.EventGroupArtist.Api.addEventGroupArtist(apiClient, { eventGroupId, artist: take<AddArtistParams>(artist, 'id', 'name', 'soundchartsUuid', 'platformUrl') });
      if (success) {
        await loadEventGroupArtists(eventGroupId);
      }

      eventGroupArtistSaving.value[eventGroupId] = LoadingState.DONE;
    } catch (e) {
      eventGroupArtistSaving.value[eventGroupId] = LoadingState.ERROR;
      throw e;
    }
  };

  const addEventArtist = async (eventId: number, artist: AddArtistParams): Promise<API.Artist.EventArtist.EventArtist> => {
    try {
      eventArtistSaving.value[eventId] = LoadingState.LOADING;
      if (!artist.platformUrl && isUrl(artist.name!)) {
        artist.platformUrl = artist.name;
      }

      const { success, data } = await API.Artist.Api.addEventArtist(apiClient, { eventId, artist: take<AddArtistParams>(artist, 'id', 'name', 'soundchartsUuid', 'platformUrl') });

      if (success) {
        eventArtistSaving.value[eventId] = LoadingState.DONE;
        return data;
      }
      throw new Error('Error adding artist');
    } catch (e) {
      eventArtistSaving.value[eventId] = LoadingState.ERROR;
      throw e;
    }
  };

  const removeEventArtist = async (eventArtistId: number) => {
    try {
      eventArtistRemoving.value[eventArtistId] = LoadingState.LOADING;

      const { success } = await API.Artist.Api.removeEventArtist(apiClient, { eventArtistId });

      if (success) {
        eventArtistRemoving.value[eventArtistId] = LoadingState.DONE;
        return;
      }
      throw new Error('Error removing artist');
    } catch (e) {
      eventArtistRemoving.value[eventArtistId] = LoadingState.ERROR;
      throw e;
    }
  };

  const reorderEventArtists = async (eventId: number, eventArtistIds: number[]): Promise<API.Artist.EventArtist.EventArtist[] | never> => {
    try {
      eventArtistsReordering.value[eventId] = LoadingState.LOADING;

      const { success, data } = await API.Artist.Api.reorderEventArtists(apiClient, { eventId, eventArtistIds });

      if (success) {
        eventArtistsReordering.value[eventId] = LoadingState.DONE;
        return data;
      }

      throw new Error('Error reordering artists');
    } catch (e) {
      eventArtistsReordering.value[eventId] = LoadingState.ERROR;
      throw e;
    }
  };

  return {
    eventGroupArtists,
    eventGroupArtistsLoading,

    loadEventGroupArtists,
    addEventGroupArtist,

    eventArtistSaving,
    addEventArtist,

    eventArtistRemoving,
    removeEventArtist,

    eventArtistsReordering,
    reorderEventArtists,
  };
});
